/**
 * A recorded value as one line of display text.
 *
 * Exhaustive over `ItemValue`, and typed so it stays that way: adding a value shape to
 * `@pius/contracts` breaks this function until it is handled. The row above an editor is often the
 * only place a value is read — on the overview, in a review, in an export preview — so a shape that
 * falls through to `[object Object]` is a real defect, not a cosmetic one.
 *
 * Option-bearing values are resolved to their **label** through the effective configuration, which
 * carries withheld options as well as offered ones. That is what lets an approved-but-withheld value
 * still print as "Sirach 26:1–4, 13–16" rather than as a bare key.
 */

import type { ItemValue, OptionAvailability } from '@pius/contracts';

function labelFor(options: OptionAvailability[], key: string): string {
  // Falling back to the key is the honest failure: it is what was actually chosen, and inventing
  // "Unknown" would hide the fact that the value still resolves to something real.
  return options.find((option) => option.key === key)?.label ?? key;
}

export function summarize(value: ItemValue | null, options: OptionAvailability[] = []): string {
  if (!value) return '';

  switch (value.type) {
    case 'single_option':
      return labelFor(options, value.option_key);

    case 'multi_option':
      return value.option_keys.map((key) => labelFor(options, key)).join(' · ');

    case 'free_text':
      return value.text;

    case 'scripture_ref':
      return value.citation;

    case 'person_ref':
      return value.display_name;

    case 'music_ref':
      return value.composer ? `${value.title} — ${value.composer}` : value.title;

    case 'date_time':
      // The stored local time, not a re-interpretation of it. Converting to the reader's zone here
      // would show a different hour than the one the parish recorded.
      return `${value.local_datetime.replace('T', ' ')} (${value.timezone})`;

    case 'yes_no':
      return value.value ? 'Yes' : 'No';

    case 'number':
      return String(value.value);

    case 'attachment_ref':
      return 'Attached file';
  }
}
